/**
 * Frankfurt Reinigung 24 – Preis-Berechnungslogik
 * Reine Funktionen, keine React-Abhängigkeiten.
 * Alle Werte werden als einfache Euro-Richtpreise kalkuliert. Im Frontend gibt es keine Netto-/Brutto-Auszeichnung.
 */

import {
  VAT_RATE,
  FREQUENCY_VISITS,
  HOUSING,
  HOUSING_OCCUPIED,
  BALCONIES,
  OFFICE_MAINTENANCE,
  OFFICE_STANDARD_ONETIME,
  OFFICE_DEEPCLEAN,
  STUDIOS,
  STAIRCASE,
  WINDOWS,
  SOIL_FACTORS,
  PAINTING,
} from "./pricing";

export const round2 = (value) => Math.round((Number(value) + Number.EPSILON) * 100) / 100;
const num = (value, fallback = 0) => Number.isFinite(Number(value)) ? Number(value) : fallback;
const positive = (value, fallback = 0) => Math.max(0, num(value, fallback));

export function visitsPerMonth(frequency, customVisitsPerMonth) {
  if (frequency === "individuell") return positive(customVisitsPerMonth);
  return FREQUENCY_VISITS[frequency] || 0;
}

function isRegular(frequency) {
  return visitsPerMonth(frequency) > 0;
}

function housingFrequencyFactor(frequency, customVisits) {
  if (frequency !== "individuell") return HOUSING.frequencyFactors[frequency] ?? 1;
  const visits = visitsPerMonth(frequency, customVisits);
  if (visits <= 1) return 0.80;
  if (visits <= 2) return 0.65;
  if (visits <= 4) return 0.50;
  return 0.47;
}

function officeFrequencyFactor(frequency, customVisits) {
  if (frequency !== "individuell") return OFFICE_MAINTENANCE.frequencyFactors[frequency] ?? 1;
  const visits = visitsPerMonth(frequency, customVisits);
  if (visits <= 1) return 1.30;
  if (visits <= 2) return 1.15;
  if (visits <= 4) return 1.00;
  return 0.95;
}

function staircaseFrequencyFactor(frequency, customVisits) {
  if (frequency !== "individuell") return STAIRCASE.frequencyFactors[frequency] ?? 1;
  const visits = visitsPerMonth(frequency, customVisits);
  if (visits <= 1) return 1.45;
  if (visits <= 2) return 1.15;
  if (visits <= 4) return 1.00;
  return 0.95;
}

function studioFrequencyFactor(frequency, customVisits) {
  if (frequency !== "individuell") return STUDIOS.frequencyFactors[frequency] ?? 1;
  const visits = visitsPerMonth(frequency, customVisits);
  if (visits <= 1) return 1.30;
  if (visits <= 2) return 1.15;
  if (visits <= 4) return 1.00;
  return 0.95;
}

function totalWindows(w = {}) {
  return ["standard", "klein", "zweifluegelig", "bodentief", "dachfenster"]
    .reduce((sum, key) => sum + positive(w[key]), 0);
}

function standardWindowUnit(quantity) {
  for (const tier of WINDOWS.standard) {
    if (quantity <= tier.maxQty) return tier.pricePerUnit;
  }
  return WINDOWS.standard[WINDOWS.standard.length - 1].pricePerUnit;
}

function occupiedHousingTierPrice(sqm) {
  const tier = HOUSING_OCCUPIED.tiers.find((item) => sqm <= item.maxSqm);
  return tier ? tier.price : null;
}

function occupiedHousingFrequencyFactor(frequency) {
  return HOUSING_OCCUPIED.frequencyFactors[frequency] ?? 1;
}

function calcOccupiedHousingWindows(w = {}) {
  const std = positive(w.standard);
  const klein = positive(w.klein);
  const zwei = positive(w.zweifluegelig);
  const boden = positive(w.bodentief);
  const dach = positive(w.dachfenster);
  const total = totalWindows(w);

  if (total === 0) return { amount: 0, breakdown: [], total, needsIndividualQuote: false };
  if (!w.soilLevel) {
    return { amount: 0, breakdown: [], total, needsIndividualQuote: true, message: "Bitte wählen Sie den Verschmutzungsgrad der Fenster aus." };
  }
  if (w.soilLevel === "sonder") {
    return { amount: 0, breakdown: [], total, needsIndividualQuote: true, message: "Bei Sonderverschmutzungen der Fenster benötigen wir Fotos oder eine Besichtigung." };
  }

  const includedStd = Math.min(std, HOUSING_OCCUPIED.includedStandardWindows);
  const extraStd = Math.max(0, std - includedStd);
  const otherBase = klein * WINDOWS.klein + zwei * WINDOWS.zweifluegelig + boden * WINDOWS.bodentief + dach * WINDOWS.dachfenster;
  let amount = extraStd * HOUSING_OCCUPIED.extraStandardWindow + otherBase;
  let surchargeReference = std * HOUSING_OCCUPIED.nominalIncludedWindowValue + otherBase;
  const breakdown = [];

  if (includedStd > 0) breakdown.push({ label: `${includedStd}× Standardfenster inklusive`, amount: 0 });
  if (extraStd > 0) breakdown.push({ label: `${extraStd}× weiteres Standardfenster à ${HOUSING_OCCUPIED.extraStandardWindow.toFixed(2)} €`, amount: round2(extraStd * HOUSING_OCCUPIED.extraStandardWindow) });
  if (klein > 0) breakdown.push({ label: `${klein}× kleines WC-/Kellerfenster`, amount: round2(klein * WINDOWS.klein) });
  if (zwei > 0) breakdown.push({ label: `${zwei}× zweiflügeliges Fenster`, amount: round2(zwei * WINDOWS.zweifluegelig) });
  if (boden > 0) breakdown.push({ label: `${boden}× bodentiefes Fenster/Balkontür`, amount: round2(boden * WINDOWS.bodentief) });
  if (dach > 0) breakdown.push({ label: `${dach}× Dachfenster`, amount: round2(dach * WINDOWS.dachfenster) });

  if (w.schwerErreichbar) {
    const surcharge = surchargeReference * WINDOWS.schwerErreichbarZuschlagPct;
    amount += surcharge;
    surchargeReference += surcharge;
    breakdown.push({ label: "Schwer erreichbar (+50 %)", amount: round2(surcharge) });
  }

  const soilFactor = WINDOWS.soilFactors[w.soilLevel] ?? 1;
  if (soilFactor !== 1) {
    const surcharge = surchargeReference * (soilFactor - 1);
    amount += surcharge;
    breakdown.push({ label: `Fensterverschmutzung (×${soilFactor.toFixed(2)})`, amount: round2(surcharge) });
  }

  return { amount: round2(amount), breakdown, total, needsIndividualQuote: false };
}

function calcWindowDetail(w = {}, standalone = false) {
  const std = positive(w.standard);
  const klein = positive(w.klein);
  const zwei = positive(w.zweifluegelig);
  const boden = positive(w.bodentief);
  const dach = positive(w.dachfenster);
  const total = totalWindows(w);

  if (total === 0) return { amount: 0, breakdown: [], total, needsIndividualQuote: false };
  if (!w.soilLevel) {
    return {
      amount: 0,
      breakdown: [],
      total,
      needsIndividualQuote: true,
      message: "Bitte wählen Sie den Verschmutzungsgrad der Fenster aus.",
    };
  }
  if (w.soilLevel === "sonder") {
    return {
      amount: 0,
      breakdown: [],
      total,
      needsIndividualQuote: true,
      message: "Bei Sonderverschmutzungen der Fenster benötigen wir Fotos oder eine Besichtigung.",
    };
  }

  const stdUnit = standardWindowUnit(std);
  let base = std * stdUnit
    + klein * WINDOWS.klein
    + zwei * WINDOWS.zweifluegelig
    + boden * WINDOWS.bodentief
    + dach * WINDOWS.dachfenster;
  const breakdown = [];

  if (std > 0) breakdown.push({ label: `${std}× Standardfenster à ${stdUnit.toFixed(2)} €`, amount: round2(std * stdUnit) });
  if (klein > 0) breakdown.push({ label: `${klein}× kleines WC-/Kellerfenster`, amount: round2(klein * WINDOWS.klein) });
  if (zwei > 0) breakdown.push({ label: `${zwei}× zweiflügeliges Fenster`, amount: round2(zwei * WINDOWS.zweifluegelig) });
  if (boden > 0) breakdown.push({ label: `${boden}× bodentiefes Fenster/Balkontür`, amount: round2(boden * WINDOWS.bodentief) });
  if (dach > 0) breakdown.push({ label: `${dach}× Dachfenster`, amount: round2(dach * WINDOWS.dachfenster) });

  if (w.schwerErreichbar) {
    const surcharge = base * WINDOWS.schwerErreichbarZuschlagPct;
    base += surcharge;
    breakdown.push({ label: "Schwer erreichbar (+50 %)", amount: round2(surcharge) });
  }

  const soilFactor = WINDOWS.soilFactors[w.soilLevel] ?? 1;
  if (soilFactor !== 1) {
    const surcharge = base * (soilFactor - 1);
    base += surcharge;
    breakdown.push({ label: `Fensterverschmutzung (×${soilFactor.toFixed(2)})`, amount: round2(surcharge) });
  }

  if (standalone && base < WINDOWS.standaloneMinPrice) {
    breakdown.push({ label: "Mindestauftragswert Fensterreinigung", amount: round2(WINDOWS.standaloneMinPrice - base) });
    base = WINDOWS.standaloneMinPrice;
  }

  return { amount: round2(base), breakdown, total, needsIndividualQuote: false };
}

export function calcWindowPrice(w, standalone = false) {
  return calcWindowDetail(w, standalone).amount;
}

function calcBalconyDetail(balconies = []) {
  if (!Array.isArray(balconies) || balconies.length === 0) {
    return { amount: 0, breakdown: [], needsIndividualQuote: false };
  }

  const breakdown = [];
  let amount = 0;
  const messages = [];

  balconies.forEach((balcony, index) => {
    if (!balcony?.size || !balcony?.soilLevel || !balcony?.weeds) {
      messages.push(`Bitte vervollständigen Sie die Angaben für Balkon ${index + 1}.`);
      return;
    }
    if (balcony.size === "sehrGross" || balcony.specialCase) {
      messages.push(`Balkon ${index + 1} muss anhand von Fotos oder bei einer Besichtigung geprüft werden.`);
      return;
    }
    const base = BALCONIES.baseBySize[balcony.size];
    const factor = BALCONIES.soilFactors[balcony.soilLevel];
    const weeds = BALCONIES.weedSurcharges[balcony.weeds];
    const price = base * factor + weeds;
    amount += price;
    breakdown.push({ label: `Balkon/Terrasse ${index + 1}`, amount: round2(price) });
  });

  return {
    amount: round2(amount),
    breakdown,
    needsIndividualQuote: messages.length > 0,
    message: messages.join(" "),
  };
}

function applySoilFactor(base, breakdown, soilLevel) {
  if (soilLevel === "extrem") {
    return {
      base,
      needsIndividualQuote: true,
      message: "Für dieses Objekt ist eine individuelle Prüfung anhand von Fotos oder einer Besichtigung erforderlich.",
    };
  }
  const factor = SOIL_FACTORS[soilLevel] ?? 1;
  if (factor !== 1) {
    const surcharge = base * (factor - 1);
    breakdown.push({ label: `Verschmutzungszuschlag (×${factor.toFixed(2)})`, amount: round2(surcharge) });
    base += surcharge;
  }
  return { base, needsIndividualQuote: false };
}

function calcHousing(input) {
  const sqm = positive(input.sqm);
  const baeder = Math.max(1, positive(input.baeder, 1));
  const gaesteWC = positive(input.gaesteWC);
  const etagen = Math.max(1, positive(input.etagen, 1));
  const occupied = input.housingCleaningType === "occupied";
  const breakdown = [];

  if (occupied) {
    if (sqm > HOUSING_OCCUPIED.maxAutomaticSqm) {
      return {
        amount: 0,
        breakdown: [],
        needsIndividualQuote: true,
        message: "Für bewohnte Wohnungen über 130 m² erstellen wir nach Prüfung der Angaben ein individuelles Angebot.",
        handlesWindows: true,
      };
    }

    let core = occupiedHousingTierPrice(sqm) || 0;
    breakdown.push({ label: `Bewohnte Wohnung – Standardreinigung (bis ${sqm} m²)`, amount: round2(core) });

    const extraBaths = Math.max(0, baeder - 1);
    if (extraBaths) {
      const price = extraBaths * HOUSING_OCCUPIED.extraBathroom;
      core += price;
      breakdown.push({ label: `${extraBaths}× weiteres Badezimmer`, amount: round2(price) });
    }
    if (gaesteWC) {
      const price = gaesteWC * HOUSING_OCCUPIED.guestWC;
      core += price;
      breakdown.push({ label: `${gaesteWC}× Gäste-WC`, amount: round2(price) });
    }

    const soil = applySoilFactor(core, breakdown, input.soilLevel);
    core = soil.base;

    if (input.objectType === "haus" && etagen >= 2) {
      const price = core * HOUSING.extras.hausMehretagenAufschlagPct;
      core += price;
      breakdown.push({ label: "Mehretagiges Haus (+10 %)", amount: round2(price) });
    }
    if (input.tierhaare) {
      const price = core * HOUSING.extras.tierhaareAufschlagPct;
      core += price;
      breakdown.push({ label: "Tierhaare (+10 %)", amount: round2(price) });
    }
    if (input.raucher) {
      const price = core * HOUSING.extras.raucherobjektAufschlagPct;
      core += price;
      breakdown.push({ label: "Raucherobjekt (+10 %)", amount: round2(price) });
    }

    const factor = occupiedHousingFrequencyFactor(input.frequency);
    if (factor !== 1) {
      const before = core;
      core *= factor;
      breakdown.push({ label: `Turnusfaktor (×${factor.toFixed(2)})`, amount: round2(core - before) });
    }

    let base = core;
    if (input.backofen) {
      base += HOUSING.extras.backofenInnen;
      breakdown.push({ label: "Backofen innen", amount: HOUSING.extras.backofenInnen });
    }
    if (input.kuehlschrank) {
      base += HOUSING.extras.kuehlschrankInnen;
      breakdown.push({ label: "Kühlschrank innen", amount: HOUSING.extras.kuehlschrankInnen });
    }
    if (input.kuechenschraenke) {
      base += HOUSING.extras.kuechenschraenkeInnen;
      breakdown.push({ label: "Küchenschränke innen", amount: HOUSING.extras.kuechenschraenkeInnen });
    }

    const balcony = calcBalconyDetail(input.balconies);
    base += balcony.amount;
    breakdown.push(...balcony.breakdown);

    const windows = calcOccupiedHousingWindows(input.fenster);
    if (!windows.needsIndividualQuote) {
      base += windows.amount;
      breakdown.push(...windows.breakdown);
    }

    return {
      amount: round2(base),
      breakdown,
      needsIndividualQuote: soil.needsIndividualQuote || balcony.needsIndividualQuote || windows.needsIndividualQuote,
      message: [soil.message, balcony.message, windows.message].filter(Boolean).join(" "),
      handlesWindows: true,
    };
  }

  // Endreinigung nach Auszug – bisherige ausführliche Wohnungslogik.
  const oneTime = input.frequency === "einmalig";
  let core = sqm * HOUSING.pricePerSqm;
  breakdown.push({ label: `Endreinigung nach Auszug (${sqm} m²)`, amount: round2(core) });

  const extraBaths = Math.max(0, baeder - 1);
  if (extraBaths) {
    const price = extraBaths * HOUSING.extras.weiteresBadezimmer;
    core += price;
    breakdown.push({ label: `${extraBaths}× weiteres Badezimmer`, amount: round2(price) });
  }
  if (gaesteWC) {
    const price = gaesteWC * HOUSING.extras.gaesteWC;
    core += price;
    breakdown.push({ label: `${gaesteWC}× Gäste-WC`, amount: round2(price) });
  }

  const soil = applySoilFactor(core, breakdown, input.soilLevel);
  core = soil.base;

  if (input.objectType === "haus" && etagen >= 2) {
    const price = core * HOUSING.extras.hausMehretagenAufschlagPct;
    core += price;
    breakdown.push({ label: "Mehretagiges Haus (+10 %)", amount: round2(price) });
  }
  if (input.tierhaare) {
    const price = core * HOUSING.extras.tierhaareAufschlagPct;
    core += price;
    breakdown.push({ label: "Tierhaare (+10 %)", amount: round2(price) });
  }
  if (input.raucher) {
    const price = core * HOUSING.extras.raucherobjektAufschlagPct;
    core += price;
    breakdown.push({ label: "Raucherobjekt (+10 %)", amount: round2(price) });
  }

  if (core < HOUSING.minPrice) {
    breakdown.push({ label: "Mindestpreis-Anhebung", amount: round2(HOUSING.minPrice - core) });
    core = HOUSING.minPrice;
  }

  let base = core;
  const setup = oneTime ? HOUSING.setupOneTime : HOUSING.setupRegular;
  base += setup;
  breakdown.push({ label: "Anfahrt, Material und Rüstzeit", amount: setup });

  if (input.backofen) {
    base += HOUSING.extras.backofenInnen;
    breakdown.push({ label: "Backofen innen", amount: HOUSING.extras.backofenInnen });
  }
  if (input.kuehlschrank) {
    base += HOUSING.extras.kuehlschrankInnen;
    breakdown.push({ label: "Kühlschrank innen", amount: HOUSING.extras.kuehlschrankInnen });
  }
  if (input.kuechenschraenke) {
    base += HOUSING.extras.kuechenschraenkeInnen;
    breakdown.push({ label: "Küchenschränke innen", amount: HOUSING.extras.kuechenschraenkeInnen });
  }

  const balcony = calcBalconyDetail(input.balconies);
  base += balcony.amount;
  breakdown.push(...balcony.breakdown);

  return {
    amount: round2(base),
    breakdown,
    needsIndividualQuote: soil.needsIndividualQuote || balcony.needsIndividualQuote,
    message: [soil.message, balcony.message].filter(Boolean).join(" "),
    handlesWindows: false,
  };
}

function calcOffice(input) {
  const sqm = positive(input.sqm);
  const toilets = positive(input.toiletten);
  const kitchens = positive(input.kuechen);
  const oneTime = input.frequency === "einmalig";
  const breakdown = [];
  let base = 0;

  if (oneTime) {
    const cfg = input.officeOneTimeType === "grund" ? OFFICE_DEEPCLEAN : OFFICE_STANDARD_ONETIME;
    const label = input.officeOneTimeType === "grund" ? "Intensive Grundreinigung" : "Einmalige Standardreinigung";
    base = sqm * cfg.pricePerSqm;
    breakdown.push({ label: `${label} (${sqm} m²)`, amount: round2(base) });
    base += cfg.setup;
    breakdown.push({ label: "Anfahrt, Material und Rüstzeit", amount: cfg.setup });
    if (toilets) {
      const price = toilets * cfg.pricePerToilet;
      base += price;
      breakdown.push({ label: `${toilets}× Toilette`, amount: round2(price) });
    }
    if (kitchens) {
      const price = kitchens * cfg.pricePerKitchen;
      base += price;
      breakdown.push({ label: `${kitchens}× Küche/Teeküche`, amount: round2(price) });
    }
    if (base < cfg.minPrice) {
      breakdown.push({ label: "Mindestpreis-Anhebung", amount: round2(cfg.minPrice - base) });
      base = cfg.minPrice;
    }
  } else {
    base = sqm * OFFICE_MAINTENANCE.pricePerSqm;
    breakdown.push({ label: `Unterhaltsreinigung (${sqm} m²)`, amount: round2(base) });
    if (toilets) {
      const price = toilets * OFFICE_MAINTENANCE.pricePerToilet;
      base += price;
      breakdown.push({ label: `${toilets}× Toilette`, amount: round2(price) });
    }
    if (kitchens) {
      const price = kitchens * OFFICE_MAINTENANCE.pricePerKitchen;
      base += price;
      breakdown.push({ label: `${kitchens}× Küche/Teeküche`, amount: round2(price) });
    }
    if (base < OFFICE_MAINTENANCE.minPricePerVisit) {
      breakdown.push({ label: "Mindestpreis-Anhebung", amount: round2(OFFICE_MAINTENANCE.minPricePerVisit - base) });
      base = OFFICE_MAINTENANCE.minPricePerVisit;
    }
    const factor = officeFrequencyFactor(input.frequency, input.customVisitsPerMonth);
    if (factor !== 1) {
      const before = base;
      base *= factor;
      breakdown.push({ label: `Turnusfaktor (×${factor.toFixed(2)})`, amount: round2(base - before) });
    }
  }

  const soil = applySoilFactor(base, breakdown, input.soilLevel);
  return { amount: round2(soil.base), breakdown, needsIndividualQuote: soil.needsIndividualQuote, message: soil.message };
}

function calcStudio(input) {
  const sqm = positive(input.sqm);
  const toilets = positive(input.toiletten);
  const showers = positive(input.duschen);
  const changing = positive(input.umkleiden);
  const kitchens = positive(input.kuechen);
  const isFitness = input.objectType === "fitnessstudio";
  const cfg = isFitness ? STUDIOS.fitness : STUDIOS.standard;
  const oneTime = input.frequency === "einmalig";
  const breakdown = [];

  let base = sqm * (oneTime ? STUDIOS.oneTimePricePerSqm : cfg.regularPricePerSqm);
  breakdown.push({ label: `${oneTime ? "Einmalige intensive Studioreinigung" : "Studio-Unterhaltsreinigung"} (${sqm} m²)`, amount: round2(base) });

  const extras = [
    [toilets, cfg.pricePerToilet, "Toilette"],
    [showers, cfg.pricePerShower, "Dusche"],
    [changing, cfg.pricePerChangingRoom, "Umkleidebereich"],
    [kitchens, cfg.pricePerKitchen, "Küche/Teeküche"],
  ];
  extras.forEach(([qty, pricePerUnit, label]) => {
    if (!qty) return;
    const price = qty * pricePerUnit;
    base += price;
    breakdown.push({ label: `${qty}× ${label}`, amount: round2(price) });
  });
  if (input.grosseSpiegel) {
    base += cfg.largeMirrors;
    breakdown.push({ label: "Große Spiegel-/Glasflächen", amount: cfg.largeMirrors });
  }
  if (input.starkFrequentiert) {
    const price = base * (STUDIOS.highTrafficFactor - 1);
    base += price;
    breakdown.push({ label: "Stark frequentiert (+15 %)", amount: round2(price) });
  }

  const minPrice = oneTime ? STUDIOS.minOneTime : cfg.minRegular;
  if (base < minPrice) {
    breakdown.push({ label: "Mindestpreis-Anhebung", amount: round2(minPrice - base) });
    base = minPrice;
  }

  if (!oneTime) {
    const factor = studioFrequencyFactor(input.frequency, input.customVisitsPerMonth);
    if (factor !== 1) {
      const before = base;
      base *= factor;
      breakdown.push({ label: `Turnusfaktor (×${factor.toFixed(2)})`, amount: round2(base - before) });
    }
  }

  const soil = applySoilFactor(base, breakdown, input.soilLevel);
  return { amount: round2(soil.base), breakdown, needsIndividualQuote: soil.needsIndividualQuote, message: soil.message };
}

function calcStaircase(input) {
  const floors = Math.max(1, positive(input.stockwerke, 1));
  const entrances = Math.max(1, positive(input.eingaenge, 1));
  const breakdown = [];
  const perEntrance = STAIRCASE.basePerEntrance + Math.max(0, floors - 1) * STAIRCASE.addPerFloor;
  let base = perEntrance;
  breakdown.push({ label: `Treppenhaus (${floors} Stockwerke)`, amount: round2(perEntrance) });

  if (entrances > 1) {
    const extra = (entrances - 1) * perEntrance * STAIRCASE.additionalEntrancePct;
    base += extra;
    breakdown.push({ label: `${entrances - 1}× weiterer Eingang (je 85 % des Grundpreises)`, amount: round2(extra) });
  }
  if (input.aufzug) {
    base += STAIRCASE.extras.aufzug;
    breakdown.push({ label: "Aufzug", amount: STAIRCASE.extras.aufzug });
  }
  if (input.kellerflur) {
    base += STAIRCASE.extras.kellerflur;
    breakdown.push({ label: "Kellerflur", amount: STAIRCASE.extras.kellerflur });
  }
  if (input.hauseingangAussen) {
    base += STAIRCASE.extras.hauseingangAussen;
    breakdown.push({ label: "Hauseingang außen", amount: STAIRCASE.extras.hauseingangAussen });
  }

  const factor = staircaseFrequencyFactor(input.frequency, input.customVisitsPerMonth);
  if (factor !== 1) {
    const before = base;
    base *= factor;
    breakdown.push({ label: `Turnusfaktor (×${factor.toFixed(2)})`, amount: round2(base - before) });
  }

  const soil = applySoilFactor(base, breakdown, input.soilLevel);
  return { amount: round2(soil.base), breakdown, needsIndividualQuote: soil.needsIndividualQuote, message: soil.message };
}

function calcCleaning(input) {
  if (input.objectType === "sonstiges") {
    return {
      amount: 0,
      breakdown: [],
      needsIndividualQuote: true,
      message: "Für dieses individuelle Objekt erstellen wir nach Prüfung Ihrer Beschreibung ein persönliches Angebot.",
    };
  }

  let core;
  switch (input.objectType) {
    case "wohnung":
    case "haus":
      core = calcHousing(input);
      break;
    case "buero":
    case "praxis":
      core = calcOffice(input);
      break;
    case "fitnessstudio":
    case "pilatesstudio":
    case "yogastudio":
    case "studio":
      core = calcStudio(input);
      break;
    case "treppenhaus":
      core = calcStaircase(input);
      break;
    case "fenster": {
      const windows = calcWindowDetail(input.fenster, true);
      return {
        amount: windows.amount,
        breakdown: windows.breakdown,
        needsIndividualQuote: windows.needsIndividualQuote,
        message: windows.message,
      };
    }
    default:
      core = { amount: 0, breakdown: [], needsIndividualQuote: true, message: "Bitte wählen Sie eine Objektart aus." };
  }

  const windows = input.objectType !== "fenster" && !core.handlesWindows ? calcWindowDetail(input.fenster, false) : null;
  const breakdown = [...core.breakdown];
  let amount = core.amount;
  let needsIndividualQuote = !!core.needsIndividualQuote;
  const messages = [core.message].filter(Boolean);

  if (windows && windows.total > 0) {
    if (windows.needsIndividualQuote) {
      needsIndividualQuote = true;
      if (windows.message) messages.push(windows.message);
    } else {
      amount += windows.amount;
      breakdown.push({ label: "Fensterreinigung (Zusatz)", amount: windows.amount });
    }
  }

  return { amount: round2(amount), breakdown, needsIndividualQuote, message: messages.join(" ") };
}

function calcPainting(input) {
  if (input.objectType === "treppenhaus") {
    return {
      amount: 0,
      breakdown: [],
      needsIndividualQuote: true,
      message: "Malerarbeiten im Treppenhaus werden nach Fotoprüfung oder Besichtigung kalkuliert.",
    };
  }
  if (["fenster", "sonstiges"].includes(input.objectType)) {
    return {
      amount: 0,
      breakdown: [],
      needsIndividualQuote: true,
      message: "Für diese Malerarbeiten benötigen wir eine individuelle Beschreibung und Fotos.",
    };
  }
  if (["gross", "tapete", "schimmel"].includes(input.paintingCondition)) {
    return {
      amount: 0,
      breakdown: [],
      needsIndividualQuote: true,
      message: "Für diese Malerarbeiten benötigen wir Fotos oder eine Besichtigung, damit wir den Untergrund und den tatsächlichen Arbeitsumfang prüfen können.",
    };
  }

  const sqm = positive(input.paintingSqm || input.sqm);
  const scope = input.paintingScope || "waende";
  const basePricePerSqm = PAINTING.wallsPricePerSqm + (scope === "waendeDecken" ? PAINTING.ceilingPriceAddonPerSqm : 0);
  const breakdown = [];

  let price = sqm * basePricePerSqm;
  breakdown.push({ label: `${scope === "waendeDecken" ? "Wände und Decken" : "Wände"} (${sqm} m² Bodenfläche)`, amount: round2(price) });

  const colorFactor = PAINTING.wallColorFactors[input.paintingCurrentColor || "hell"] ?? 1;
  if (colorFactor !== 1) {
    const before = price;
    price *= colorFactor;
    breakdown.push({ label: `Ausgangsfarbe / Deckkraft (×${colorFactor.toFixed(2)})`, amount: round2(price - before) });
  }

  if (input.paintingHighCeiling) {
    const before = price;
    price *= PAINTING.highCeilingFactor;
    breakdown.push({ label: "Raumhöhe über 2,70 m (+15 %)", amount: round2(price - before) });
  }
  if (input.paintingFurniture) {
    const before = price;
    price *= PAINTING.furnitureFactor;
    breakdown.push({ label: "Aufwendiges Abdecken / viele Möbel (+10 %)", amount: round2(price - before) });
  }
  if (input.paintingCondition === "klein" || input.paintingSmallRepairs) {
    const before = price;
    price *= PAINTING.smallRepairsFactor;
    breakdown.push({ label: "Kleine Löcher und leichte Risse (+5 %)", amount: round2(price - before) });
  }

  if (price < PAINTING.minPrice) {
    breakdown.push({ label: "Mindestpreis-Anhebung", amount: round2(PAINTING.minPrice - price) });
    price = PAINTING.minPrice;
  }

  const needsConfirmation = input.paintingCurrentColor === "sperrgrund";
  return {
    amount: round2(price),
    breakdown,
    needsIndividualQuote: needsConfirmation,
    message: needsConfirmation
      ? "Der Richtpreis muss wegen Nikotin, Ruß, Wasserflecken oder notwendiger Sperrgrundierung nach Fotos oder Besichtigung bestätigt werden."
      : "",
  };
}

function finalize(input, components) {
  const breakdown = [];
  let netto = 0;
  let needsIndividualQuote = false;
  const messages = [];

  components.forEach((component) => {
    if (!component) return;
    netto += component.amount || 0;
    breakdown.push(...(component.breakdown || []));
    if (component.needsIndividualQuote) needsIndividualQuote = true;
    if (component.message) messages.push(component.message);
  });

  netto = round2(netto);
  const mwst = round2(netto * VAT_RATE);
  const brutto = round2(netto + mwst);
  const monthlyVisits = visitsPerMonth(input.frequency, input.customVisitsPerMonth);
  const monthly = monthlyVisits > 0 && input.serviceType === "cleaning"
    ? {
        visitsPerMonth: monthlyVisits,
        netto: round2(netto * monthlyVisits),
        mwst: round2(netto * monthlyVisits * VAT_RATE),
        brutto: round2(netto * monthlyVisits * (1 + VAT_RATE)),
      }
    : null;

  return {
    needsIndividualQuote,
    hasPrice: netto > 0,
    message: messages.join(" "),
    netto,
    mwst,
    brutto,
    price: netto,
    perVisit: netto,
    monthly: monthly ? { ...monthly, price: monthly.netto } : null,
    breakdown,
    serviceType: input.serviceType,
    objectType: input.objectType,
    frequency: input.frequency,
    soilLevel: input.soilLevel,
    meta: {
      visitsPerMonth: monthlyVisits,
      isRegular: monthlyVisits > 0,
      isPartialEstimate: needsIndividualQuote && netto > 0,
    },
  };
}

export function calculate(input = {}) {
  const serviceType = input.serviceType || "cleaning";
  const normalized = { ...input, serviceType };

  if (serviceType === "individual") {
    return finalize(normalized, [{
      amount: 0,
      breakdown: [],
      needsIndividualQuote: true,
      message: "Für Ihre individuelle Anfrage erstellen wir nach Prüfung der Angaben ein persönliches Angebot.",
    }]);
  }

  const components = [];
  if (["cleaning", "combined"].includes(serviceType)) components.push(calcCleaning(normalized));
  if (["painting", "combined"].includes(serviceType)) components.push(calcPainting(normalized));
  return finalize(normalized, components);
}

if (typeof window !== "undefined") {
  window.__fr24Calc = { calculate, calcWindowPrice, visitsPerMonth };
}
